import { useState } from 'react'
import { Button, SegmentedControl, Skeleton } from '@teach/ui'
import { PaymentsHistory } from '@/features/settings/components/PaymentsHistory'
import { Plus } from 'lucide-react'
import { formatCredits, formatCreditsWithWord, isPositiveCredits } from '@/features/ai/lib/money'
import { useGetCreditsBalanceQuery } from '@/features/ai/creditsApi'
import { CreditIcon } from '@/components/common/CreditIcon'
import { HistoryList } from './HistoryList'
import { useMessages } from '@/i18n'
import { messages } from './messages'

type Tab = 'usage' | 'payments'

interface Props {
  onTopUp: () => void
}

/**
 * Окно кредитов: крупный баланс, заморозка, кнопка покупки и история движений.
 * Заморозка показывается только когда она есть — строка «заморожено 0» ничего
 * не объясняет и добавляет шума в спокойное состояние.
 */
export const CreditsOverview = ({ onTopUp }: Props) => {
  const { data, isError } = useGetCreditsBalanceQuery()
  const m = useMessages(messages)
  const [tab, setTab] = useState<Tab>('usage')
  // Чтение защитное: неполный ответ не должен ронять окно (та же манера, что у
  // `useTranslationQuote` и чипа шапки).
  const balance = data?.balance
  const held = isPositiveCredits(data?.held) ? (data?.held ?? null) : null

  return (
    <div className="min-w-0 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          {isError || (data !== undefined && !balance) ? (
            <p className="text-3xl font-bold">—</p>
          ) : balance ? (
            <p className="flex items-center gap-2.5 text-3xl font-bold">
              <CreditIcon className="size-8" />
              {formatCreditsWithWord(balance)}
            </p>
          ) : (
            <Skeleton className="h-9 w-40" />
          )}
          {held ? (
            <p className="text-xs text-muted-foreground">{m.held(formatCredits(held))}</p>
          ) : null}
        </div>
        <Button size="lg" className="shrink-0" onClick={onTopUp}>
          <Plus className="mr-2 size-5" />
          {m.topUp}
        </Button>
      </div>
      {/* Как в профиле («Использование»): движения кредитов и заказы на покупку —
          разные данные, поэтому переключатель, а не один список. */}
      <SegmentedControl<Tab>
        options={[
          { id: 'usage', label: m.tabUsage },
          { id: 'payments', label: m.tabPayments },
        ]}
        value={tab}
        onChange={setTab}
      />
      <div className="max-h-[50vh] min-w-0 overflow-y-auto rounded-2xl bg-foreground/5 p-5">
        {tab === 'usage' ? <HistoryList /> : <PaymentsHistory />}
      </div>
    </div>
  )
}
